/**
 * Study configuration produced by the Setup wizard and consumed by the
 * Participant experience. Schema mirrors the prototype's `defaultStudy`
 * in Study Setup.html and the `STUDY` object in Participant Experience.html.
 *
 * Keep these two surfaces in lockstep — Participant reads what Setup writes.
 */

export type StudyQuestion = {
  id: number;
  text: string;
};

export type BriefSection = {
  id: string;
  title: string;
  hint?: string;
  body: string;
  /** custom sections were added by the researcher (vs. preset titles) */
  custom?: boolean;
};

export type DeploymentMode = 'standalone' | 'embedded';

export type Study = {
  goal: string;
  /** Where participants will reach this study from. Drives whether brief +
   *  consent are shown on the welcome page and whether the end page redirects
   *  back to a survey. Asked very early in the wizard so it can gate later
   *  steps (e.g. Brief is only relevant in standalone mode). */
  deploymentMode: DeploymentMode | '';
  /** Where to send embedded-mode participants when the interview ends.
   *  Only used when deploymentMode === 'embedded'. */
  redirectUrl: string;
  decision: string;
  decisionCustom: string;
  interests: string[];
  background: string;
  questions: StudyQuestion[];
  maxFollowUps: number;
  opening: string;
  closing: string;
  brief: BriefSection[];
  /** Shown in the quick-facts strip on the participant welcome page. */
  duration: number;
  compensation: string;
  /** Shown in the "Research team" block on the participant welcome page. */
  researcher: string;
  affiliation: string;
  contactEmail: string;
  irb: string;
};

export const DEFAULT_STUDY: Study = {
  goal: '',
  deploymentMode: '',
  redirectUrl: '',
  decision: '',
  decisionCustom: '',
  interests: [],
  background: '',
  questions: [],
  maxFollowUps: 2,
  opening: '',
  closing: '',
  duration: 20,
  compensation: '',
  researcher: '',
  affiliation: '',
  contactEmail: '',
  irb: '',
  brief: [
    {
      id: 'b1',
      title: 'Purpose of the study',
      hint: 'Why are you doing this study? What will the findings be used for?',
      body: '',
    },
    {
      id: 'b2',
      title: "What you'll be asked to do",
      hint: 'Walk participants through the interview format and what to expect.',
      body: '',
    },
    {
      id: 'b3',
      title: 'Eligibility criteria',
      hint: 'Who can take part? List any age, location, or experience requirements.',
      body: '',
    },
    {
      id: 'b4',
      title: 'Risks & benefits',
      hint: 'Note any sensitive topics. Be honest if there are no direct benefits.',
      body: '',
    },
    {
      id: 'b5',
      title: 'How we handle your data',
      hint: 'Where is data stored? Who can see it? When is it deleted?',
      body: '',
    },
    {
      id: 'b6',
      title: 'Voluntary participation',
      hint: 'Make clear participants can stop at any time without penalty.',
      body: '',
    },
    {
      id: 'b7',
      title: 'Compensation',
      hint: 'Gift card, payment, course credit — or "none" if applicable.',
      body: '',
    },
  ],
};

export const TOTAL_STEPS = 12;

/** Brief is only relevant in standalone mode — embedded participants come
 *  from Qualtrics where consent + briefing already happened. The wizard
 *  navigation skips this step when embedded. */
export const BRIEF_STEP = 11;

